<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import DatePicker from 'vue-datepicker-next';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useLocalizedTime } from 'shared/composables/useLocalizedTime';
import { useAudiencesStore } from 'dashboard/stores/audiences';
import InboxHealthAPI from 'dashboard/api/inboxHealth';

import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AudiencePicker from 'dashboard/components-next/Audiences/AudiencePicker.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const { t } = useI18n();
const { dateTime } = useLocalizedTime();
const route = useRoute();
const router = useRouter();
const store = useStore();
const audiencesStore = useAudiencesStore();

const inboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const inboxOptions = computed(() =>
  inboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const form = ref({
  title: '',
  inboxId: null,
  audienceId: '',
  templateId: null,
});

// Same friendly-name formatting as the legacy one-off campaign form, for
// visual consistency between the two.
const templateOptions = computed(() => {
  if (!form.value.inboxId) return [];
  const templates = getFilteredWhatsAppTemplates.value(form.value.inboxId);
  return templates.map(template => {
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template,
    };
  });
});

const selectedTemplate = computed(
  () =>
    templateOptions.value.find(option => option.value === form.value.templateId)
      ?.template || null
);

// The template belongs to a specific inbox - clear it when the channel
// changes so a stale selection from another inbox can't linger.
watch(
  () => form.value.inboxId,
  () => {
    form.value.templateId = null;
  }
);

const templateParserRef = ref(null);

// Informational only (decision 2026-10-06): the Meta quota/quality tiers
// aren't enforced here, just surfaced so the operator can judge whether the
// audience size is realistic for this channel before dispatching.
const QUALITY_COLORS = {
  GREEN: 'text-n-teal-11',
  YELLOW: 'text-n-amber-11',
  RED: 'text-n-ruby-11',
  UNKNOWN: 'text-n-slate-11',
};

const channelHealth = ref(null);
const isLoadingHealth = ref(false);

const fetchChannelHealth = async inboxId => {
  channelHealth.value = null;
  if (!inboxId) return;
  isLoadingHealth.value = true;
  try {
    const { data } = await InboxHealthAPI.getHealthStatus(inboxId);
    channelHealth.value = data;
  } catch (error) {
    channelHealth.value = null;
  } finally {
    isLoadingHealth.value = false;
  }
};

watch(() => form.value.inboxId, fetchChannelHealth);

const qualityRating = computed(
  () => channelHealth.value?.quality_rating || 'UNKNOWN'
);
const qualityColor = computed(
  () => QUALITY_COLORS[qualityRating.value] || QUALITY_COLORS.UNKNOWN
);
const tierLabel = computed(() => {
  const tier = channelHealth.value?.messaging_limit_tier;
  return tier
    ? t(`INBOX_MGMT.ACCOUNT_HEALTH.VALUES.TIERS.${tier}`)
    : t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.TIERS.UNKNOWN');
});

// Audience summary for the review step - the picker only carries the id, so
// the saved audience's own criteria has to be fetched before it can be
// previewed (same preview endpoint/counts as the Audiences screen).
const audienceSummary = ref(null);
const isLoadingAudienceSummary = ref(false);

watch(
  () => form.value.audienceId,
  async audienceId => {
    audienceSummary.value = null;
    if (!audienceId) return;
    isLoadingAudienceSummary.value = true;
    try {
      const audience = await audiencesStore.show(audienceId);
      const preview = await audiencesStore.preview(audience.criteria);
      audienceSummary.value = {
        name: audience.name,
        count: preview.count,
        withoutPhoneCount: preview.withoutPhoneCount,
      };
    } catch (error) {
      audienceSummary.value = null;
    } finally {
      isLoadingAudienceSummary.value = false;
    }
  }
);

const isScheduled = ref(false);
const scheduledAt = ref(new Date());

const disablePastDates = date =>
  date < new Date(new Date().setHours(0, 0, 0, 0));

const isSubmitting = ref(false);
const isSubmitted = ref(false);

const isFormValid = computed(() => {
  if (!form.value.title.trim()) return false;
  if (!form.value.inboxId) return false;
  if (!form.value.audienceId) return false;
  if (!selectedTemplate.value) return false;
  if (isScheduled.value && !scheduledAt.value) return false;
  return true;
});

const handleCreateCampaign = async () => {
  isSubmitted.value = true;
  templateParserRef.value?.v$?.$touch();
  if (!isFormValid.value || templateParserRef.value?.v$?.$invalid) return;

  const { name, category, language, namespace } = selectedTemplate.value;
  const payload = {
    title: form.value.title.trim(),
    message: templateParserRef.value.renderedTemplate,
    inbox_id: form.value.inboxId,
    audience_id: form.value.audienceId,
    template_params: {
      name,
      category,
      language,
      namespace,
      processed_params: templateParserRef.value.processedParams,
    },
  };
  if (isScheduled.value) {
    payload.scheduled_at = new Date(scheduledAt.value).toISOString();
  }

  isSubmitting.value = true;
  try {
    await store.dispatch('campaigns/create', payload);
    useAlert(t('CAMPAIGN_CREATE.SUCCESS_MESSAGE'));
    router.push({
      name: 'campaigns_whatsapp_index',
      params: { accountId: route.params.accountId },
    });
  } catch (error) {
    useAlert(t('CAMPAIGN_CREATE.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="flex items-center w-full max-w-3xl mx-auto h-20">
        <span class="text-heading-1 text-n-slate-12">
          {{ t('CAMPAIGN_CREATE.HEADER_TITLE') }}
        </span>
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="grid w-full max-w-3xl gap-6 py-4 mx-auto">
        <Input
          v-model="form.title"
          :label="t('CAMPAIGN_CREATE.FIELDS.TITLE_LABEL')"
          :placeholder="t('CAMPAIGN_CREATE.FIELDS.TITLE_PLACEHOLDER')"
        />

        <div class="grid gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN_CREATE.FIELDS.CHANNEL_LABEL') }}
          </span>
          <ComboBox
            v-model="form.inboxId"
            :options="inboxOptions"
            :placeholder="t('CAMPAIGN_CREATE.FIELDS.CHANNEL_PLACEHOLDER')"
          />
          <div v-if="isLoadingHealth" class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN_CREATE.CHANNEL_HEALTH.LOADING') }}
          </div>
          <div
            v-else-if="channelHealth"
            class="grid gap-1 p-3 text-xs border rounded-lg border-n-weak bg-n-alpha-1"
          >
            <span>
              <span class="text-n-slate-11">{{
                t('CAMPAIGN_CREATE.CHANNEL_HEALTH.QUALITY_LABEL')
              }}</span>
              <span :class="qualityColor" class="font-medium">{{
                qualityRating
              }}</span>
            </span>
            <span class="text-n-slate-11">
              {{ t('CAMPAIGN_CREATE.CHANNEL_HEALTH.TIER_LABEL') }}
              <span class="text-n-slate-12">{{ tierLabel }}</span>
            </span>
          </div>
        </div>

        <div class="grid gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN_CREATE.FIELDS.AUDIENCE_LABEL') }}
          </span>
          <AudiencePicker v-model="form.audienceId" />
        </div>

        <div v-if="form.inboxId" class="grid gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN_CREATE.FIELDS.TEMPLATE_LABEL') }}
          </span>
          <ComboBox
            v-model="form.templateId"
            :options="templateOptions"
            :placeholder="t('CAMPAIGN_CREATE.FIELDS.TEMPLATE_PLACEHOLDER')"
          />
          <WhatsAppTemplateParser
            v-if="selectedTemplate"
            ref="templateParserRef"
            :template="selectedTemplate"
            allow-dynamic-fields
          />
        </div>

        <div v-if="selectedTemplate" class="grid gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN_CREATE.FIELDS.SCHEDULE_LABEL') }}
          </span>
          <div class="grid gap-3 sm:grid-cols-2">
            <RadioCard
              id="send-now"
              :label="t('CAMPAIGN_CREATE.SCHEDULE.NOW')"
              :description="t('CAMPAIGN_CREATE.SCHEDULE.NOW_DESCRIPTION')"
              :is-active="!isScheduled"
              @select="isScheduled = false"
            />
            <RadioCard
              id="send-later"
              :label="t('CAMPAIGN_CREATE.SCHEDULE.LATER')"
              :description="t('CAMPAIGN_CREATE.SCHEDULE.LATER_DESCRIPTION')"
              :is-active="isScheduled"
              @select="isScheduled = true"
            />
          </div>
          <DatePicker
            v-if="isScheduled"
            v-model:value="scheduledAt"
            type="datetime"
            confirm
            :clearable="false"
            :editable="false"
            :placeholder="t('CAMPAIGN_CREATE.SCHEDULE.PLACEHOLDER')"
            input-class="mx-input w-full"
            :disabled-date="disablePastDates"
          />
        </div>

        <div
          v-if="selectedTemplate"
          class="grid gap-2 p-4 border rounded-lg border-n-weak bg-n-alpha-1"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN_CREATE.REVIEW.TITLE') }}
          </span>
          <div v-if="isLoadingAudienceSummary" class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN_CREATE.REVIEW.LOADING_AUDIENCE') }}
          </div>
          <template v-else-if="audienceSummary">
            <span class="text-sm text-n-slate-11">
              {{
                t('CAMPAIGN_CREATE.REVIEW.AUDIENCE_SUMMARY', {
                  name: audienceSummary.name,
                  count: audienceSummary.count,
                })
              }}
            </span>
            <span
              v-if="audienceSummary.withoutPhoneCount"
              class="text-sm text-n-amber-11"
            >
              {{
                t('CAMPAIGN_CREATE.REVIEW.SKIPPED_NO_PHONE', {
                  count: audienceSummary.withoutPhoneCount,
                })
              }}
            </span>
          </template>
          <span class="text-sm text-n-slate-11">
            {{
              t('CAMPAIGN_CREATE.REVIEW.TEMPLATE_SUMMARY', {
                name: selectedTemplate.name,
              })
            }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{
              isScheduled
                ? t('CAMPAIGN_CREATE.REVIEW.SCHEDULED_SUMMARY', {
                    datetime: dateTime(new Date(scheduledAt).getTime() / 1000),
                  })
                : t('CAMPAIGN_CREATE.REVIEW.NOW_SUMMARY')
            }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('CAMPAIGN_CREATE.REVIEW.CONSENT_NOTICE') }}
          </span>
        </div>

        <div v-if="selectedTemplate" class="flex">
          <NextButton
            :label="t('CAMPAIGN_CREATE.SUBMIT')"
            color="blue"
            :is-loading="isSubmitting"
            :disabled="isSubmitted && !isFormValid"
            @click="handleCreateCampaign"
          />
        </div>
      </div>
    </main>
  </section>
</template>
